<template lang="pug">
    .example
        .example__container(
            :style="exampleStyle"
        )
            template(v-for="(number, index) in parsedExample.length")
                span(:class="getExampleElementStyle(index)") {{ getExampleElement(index) + ' ' }}
                wbr(v-if="allowLineBreak(index)")
        template(v-if="!dictation")
            .example__answer
                template(v-if="!playersNumber")
                    span.example__answer-header(v-if="!checkedWidthOfDevice")
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_enterTheAnswer') }}
                    span.example__answer-header(v-else)
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_answerForMulAndDiv') }}
                cs-input.example__input(
                    @input.prevent.native = "answer = handleDigitInput($event)"
                    @keyup.native.enter="checkAnswer(playersNumber > 1)"
                    ref="input"
                    :disabled="!startGame"
                    :correct="correct"
                    :wrong="!correct && !startGame"
                )
            cs-button(
                @click="checkAnswer(playersNumber > 1)",
                color="blue"
                :disabled="!startGame"
            )
                | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
</template>

<script>
    import handleDigitInput from '../../../../utils/trainer/handleDigitInput';

    const OPERATIONS = ['+', '-', '×', '÷'];

    export default {
        props: {
            example: String,
            checkMultiplayerAnswers: Boolean,
            dictation: Boolean,
            fractions: {
                type: Boolean,
                default: false,
            },
            playersNumber: Number,
            colorForStudent: String,
            startGame: {
                type: Boolean,
                default: false,
            },
            correct: {
                type: Boolean,
                default: false,
            },
            isBattle: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                parsedExample: [],
                numbers: [],
                operations: [],
                openBrackets: [],
                closeBrackets: [],
                answer: '',
                isFocused: false,
                focusTimeout: null,
            };
        },
        mounted() {
            this.stringExampleToArray();
            this.focusTimeout = setTimeout(() => {
                this.$refs.input?.$el.focus();
            }, 200);
        },
        watch: {
            example: {
                handler() {
                    this.$nextTick(() => {
                        this.$refs.input.$el.value = '';
                        this.answer = null;
                    });
                },
                deep: true,
            },
            checkMultiplayerAnswers(val) {
                if (val) {
                    this.checkAnswer(true);
                }
            },
        },
        updated() {
            if ((!this.isFocused && this.$refs.input) || this.startGame) {
                this.$refs.input?.$el.focus();
                this.isFocused = true;
            }
        },
        computed: {
            checkedWidthOfDevice() {
                let width = window.innerWidth;
                return width < 768;
            },
            exampleStyle() {
                return {
                    color: this.playersNumber !== 0 && this.colorForStudent,
                };
            },
        },
        methods: {
            stringExampleToArray() {
                let splittedExample = this.example.split('');
                let item = splittedExample[0];
                for (let i = 0; i < splittedExample.length - 1; i++) {
                    if (this.isNumericOrDot(item) && this.isNumericOrDot(splittedExample[i + 1])) {
                        item += splittedExample[i + 1];
                    } else {
                        this.parsedExample.push(item);
                        item = splittedExample[i + 1];
                    }
                }
                this.parsedExample.push(item);
            },
            allowLineBreak(index) {
                return OPERATIONS.includes(this.getExampleElement(index));
            },
            getExampleElement(index) {
                if (this.parsedExample[index] === '*') {
                    return '×';
                }
                if (this.parsedExample[index] === '/') {
                    return '÷';
                }
                return this.parsedExample[index];
            },
            getExampleElementStyle(index) {
                const exampleElementStyle = {
                    example__element_large: this.example.length < 20,
                    example__element_medium: this.example.length >= 20 && this.example.length < 35,
                    example__element_small: this.example.length >= 35,
                };

                switch (this.getExampleElement(index)) {
                    case '(':
                    case ')':
                        exampleElementStyle['example__bracket'] = true;
                        break;
                    case '+':
                    case '-':
                    case '×':
                    case '÷':
                        exampleElementStyle['example__operation'] = true;
                        break;
                    default:
                        exampleElementStyle['example__number'] = true;
                }
                return exampleElementStyle;
            },
            isNumericOrDot(n) {
                return (!isNaN(parseFloat(n)) && isFinite(n)) || n === '.';
            },
            checkAnswer(isMultiplayer = false) {
                const removeTrailingZeros = () => this.answer?.replace(/(\.[0-9]*[1-9])0+$|\.0*$/, '$1');
                this.answer = removeTrailingZeros(this.answer?.replace(',', '.'));
                if (this.answer || this.answer === 0 || isMultiplayer) {
                    document.activeElement.blur();
                    this.isFocused = false;
                    this.$emit('answer', this.answer);
                }
            },
            handleDigitInput,
        },
        beforeDestroy() {
            clearTimeout(this.focusTimeout);
        },
    };
</script>

<style scoped lang="scss">
    .example {
        text-align: center;
    }

    .example__container {
        font-size: $font-size-44;
        font-weight: 500;
        white-space: nowrap;
        margin-bottom: 20px;
    }

    .example__container_size-big {
        font-size: $font-size-32;
        @include media($sm) {
            font-size: $font-size-76;
        }
        @include media($md) {
            font-size: $font-size-100;
        }
        @include media($lg) {
            font-size: $font-size-120;
        }
    }

    .example__container_size-large {
        font-size: $font-size-20;
        @include media($sm) {
            font-size: $font-size-44;
        }
        @include media($md) {
            font-size: $font-size-76;
        }
        @include media($lg) {
            font-size: $font-size-100;
        }
    }

    .example__container_size-medium {
        font-size: $font-size-22;
        @include media($sm) {
            font-size: $font-size-26;
        }
        @include media($md) {
            font-size: $font-size-44;
        }
        @include media($lg) {
            font-size: $font-size-56;
        }
    }

    .example__answer {
        display: flex;
        flex-direction: column;
        align-items: center;
        @include stack-space($space-base);
    }

    .example__answer-header {
        @include stack-space($space-base);
        font-size: 22px;
        font-weight: 500;
        white-space: nowrap;
        @include media($md) {
            font-size: 32px;
        }
    }

    .example__input {
        max-width: 170px;
        margin-right: 10px;
        height: 80px;
        font-size: 22px !important;
        text-align: center;
        @include media($md) {
            max-width: 280px;
            font-size: 32px !important;
        }
    }

    .example__element_large {
        font-size: $font-size-18;

        @include media($sm) {
            font-size: $font-size-30;
        }

        @include media($md) {
            font-size: $font-size-36;
        }

        @include media($lg) {
            font-size: $font-size-44;
        }

        @include media($xlg) {
            font-size: $font-size-56;
        }
    }

    .example__element_medium {
        font-size: $font-size-14;

        @include media($sm) {
            font-size: $font-size-18;
        }

        @include media($md) {
            font-size: $font-size-30;
        }

        @include media($lg) {
            font-size: $font-size-36;
        }

        @include media($xlg) {
            font-size: $font-size-44;
        }
    }

    .example__element_small {
        font-size: $font-size-12;

        @include media($sm) {
            font-size: $font-size-14;
        }

        @include media($md) {
            font-size: $font-size-18;
        }

        @include media($lg) {
            font-size: $font-size-22;
        }

        @include media($xlg) {
            font-size: $font-size-28;
        }
    }

    .example__operation {
        color: $hsl-color-orange-56;
    }

    .example__bracket {
        color: $hsl-color-green-58;
    }

    .example__number {
        color: $hsl-color-blue-56;
    }

    $players-amount-sizes: (
        '12': (
            1,
        ),
        '6': (
            2,
        ),
        '4': (
            3,
        ),
        '3': (
            4,
        ),
    );
    .multiplayer {
        .example {
            .cs-button {
                font-weight: 500;
            }
        }
    }
    @each $col-size, $size in $players-amount-sizes {
        .multiplayer__item--is-#{$col-size} .example__container {
            @if $size == 2 {
                @include media($xlg) {
                    font-size: 38px;
                }
                @include media($xl) {
                    font-size: 46px;
                }
                @include media($xxl) {
                    font-size: 70px;
                }
            }
            @if $size == 3 {
                @include media($xlg) {
                    font-size: 24px;
                }
                @include media($xl) {
                    font-size: 32px;
                }
                @include media($xxl) {
                    font-size: 46px;
                }
            }
            @if $size == 4 {
                @include media($xl) {
                    font-size: 24px;
                }
                @include media($xxl) {
                    font-size: 36px;
                }
            }
        }
        .multiplayer--multiline .multiplayer__item--is-#{$col-size} .example__container {
            @if $size == 2 {
                @include media($xlg) {
                    font-size: 28px;
                }
                @include media($xl) {
                    font-size: 46px;
                }
            }
            @if $size == 3 {
                @include media($xlg) {
                    font-size: 24px;
                }
                @include media($xl) {
                    font-size: 32px;
                }
                @include media($xxl) {
                    font-size: 46px;
                }
            }
            @if $size == 4 {
                @include media($xlg) {
                    font-size: 18px;
                }
                @include media($xl) {
                    font-size: 24px;
                }
                @include media($xxl) {
                    font-size: 36px;
                }
            }
        }
        .multiplayer__item--is-#{$col-size} .example__input {
            @if $size == 2 {
                @include media($xlg) {
                    max-width: 250px;
                    max-height: 70px;
                }
                @include media($xl) {
                    max-height: 80px;
                }
            } @else if $size == 3 {
                @include media($xlg) {
                    max-width: 180px;
                    max-height: 42px;
                }
                @include media($xl) {
                    max-width: 250px;
                    max-height: 60px;
                }
                @include media($xxl) {
                    max-width: 250px;
                    max-height: 80px;
                }
            } @else if $size == 4 {
                @include media($xlg) {
                    max-width: 150px;
                    max-height: 40px;
                }
                @include media($xl) {
                    max-width: 250px;
                    max-height: 50px;
                }
                @include media($xxl) {
                    max-width: 250px;
                    max-height: 80px;
                }
            }
        }
        .multiplayer--multiline .multiplayer__item--is-#{$col-size} .example__input {
            @if $size == 2 {
                @include media($xlg) {
                    max-width: 150px;
                    max-height: 40px;
                }
                @include media($xl) {
                    max-width: 250px;
                    max-height: 50px;
                }
            } @else if $size == 3 {
                @include media($xlg) {
                    max-width: 150px;
                    max-height: 40px;
                }
                @include media($xl) {
                    max-width: 250px;
                    max-height: 50px;
                }
                @include media($xxl) {
                    max-width: 250px;
                    max-height: 60px;
                }
            } @else if $size == 4 {
                @include media($xlg) {
                    max-width: 110px;
                    max-height: 32px;
                }
                @include media($xl) {
                    max-width: 180px;
                    max-height: 42px;
                }
                @include media($xxl) {
                    max-width: 250px;
                    max-height: 60px;
                }
            }
        }
    }
</style>
